import { useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useCallState } from '../context/CallStateContext';
import { usePractice } from '../context/PracticeContext';
import { getStatus } from '../utils/supportApi';

/**
 * Is the support chat on for this practice and this user?
 * Returns {enabled, mode, features}: mode 'test' | 'live' (null unless enabled),
 * features as the server sends them ({} unless enabled).
 *
 * The server decides: every practice has the chat, linked to a tracker client or
 * not, and the mode must admit the caller (test mode, only for a linked practice:
 * super_admins only, who try the chat as if they were practice staff; live mode:
 * owners, members, task managers and super_admins).
 *
 * Fails closed: loading, 403, 404 (a backend that predates the feature),
 * network errors and anything but an explicit `enabled: true` WITH a mode of
 * 'test' or 'live' all read as off, so the frontend can ship before the backend
 * without showing anything (a backend from before the test/live modes never
 * switches it on). No retry — a 403/404 is an answer, not a glitch.
 *
 * Several components may ask; they share one cache entry, so it is one request.
 * A link change by the team (link, unlink, release) arrives as
 * `support_changed {link: true}` and re-asks, as does every socket (re)connect.
 *
 * Asked only by a super_admin or an owner, member or task manager of the practice
 * on screen (a role the server does not know would be a 403 that the tenant
 * middleware logs as a warning on every Tasks page visit).
 */
export const supportStatusKey = (practiceId) => ['supportStatus', practiceId];

const NO_FEATURES = Object.freeze({});

export default function useSupportStatus(practiceId) {
  const queryClient = useQueryClient();
  const { subscribeSupportEvents } = useCallState();
  const { practiceRole, isUserSuperAdmin } = usePractice();
  const eligible = isUserSuperAdmin
    || practiceRole === 'owner' || practiceRole === 'member' || practiceRole === 'task_manager';

  const query = useQuery(supportStatusKey(practiceId), () => getStatus(practiceId), {
    enabled: !!practiceId && eligible,
    retry: false,
    staleTime: 10 * 60 * 1000,
    onError: () => {},
  });

  useEffect(() => {
    if (!practiceId) return undefined;
    return subscribeSupportEvents((data) => {
      if (data?.link || data?.resync) queryClient.invalidateQueries(supportStatusKey(practiceId));
    });
  }, [subscribeSupportEvents, queryClient, practiceId]);

  const data = query.data;
  const enabled = !!practiceId && eligible && !query.isError
    && data?.enabled === true && (data.mode === 'test' || data.mode === 'live');
  return {
    enabled,
    mode: enabled ? data.mode : null,
    features: enabled && data.features && typeof data.features === 'object' ? data.features : NO_FEATURES,
  };
}
